import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Spinner,
  Alert,
  ListGroup,
  Row,
  Col,
} from "react-bootstrap";
import { toast } from "react-toastify";
import { apiGet, apiDelete } from "../../api/api";

const AttachmentsModalTrash = ({
  show,
  onHide,
  taskId,
  officerName = "Officer",
  attachmentCount, // Add this new prop
}) => {
  const [attachments, setAttachments] = useState([]);
  const [previewFile, setPreviewFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const formatFileSize = (bytes) => {
    if (!bytes || isNaN(bytes)) return "0 KB";
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(2)} KB`;
    const mb = kb / 1024;
    return `${mb.toFixed(2)} MB`;
  };

  const getFileIcon = (fileName) => {
    if (fileName.match(/\.(jpg|jpeg|png|gif|webp)$/i)) {
      return <i className="bi bi-image-fill text-btn-primary" />;
    }
    if (fileName.match(/\.pdf$/i)) {
      return <i className="bi bi-file-earmark-pdf-fill text-danger" />;
    }
    if (fileName.match(/\.(doc|docx)$/i)) {
      return <i className="bi bi-file-earmark-word-fill text-btn-primary" />;
    }
    return <i className="bi bi-file-earmark-fill" />;
  };

  const renderFilePreview = (file) => {
    if (!file) return null;

    if (file.fileName.match(/\.(jpg|jpeg|png|gif|webp)$/i)) {
      return (
        <img
          src={file.url}
          alt={file.fileName}
          className="img-fluid rounded"
          style={{ maxHeight: "400px", maxWidth: "100%" }}
        />
      );
    }
    if (file.fileName.match(/\.pdf$/i)) {
      return (
        <iframe
          src={file.url}
          title={file.fileName}
          width="100%"
          height="400px"
          className="border-0"
          style={{ minHeight: "400px" }}
        />
      );
    }
    return (
      <div className="text-center py-5">
        <i className="bi bi-file-earmark-text fs-1 text-muted" />
        <p className="mt-2">Preview not available for this file type</p>
      </div>
    );
  };

  const downloadFile = (file) => {
    if (!file) return;
    const link = document.createElement("a");
    link.href = file.url;
    link.download = file.fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleClose = () => {
    setPreviewFile(null);
    onHide();
  };

  const fetchAttachments = async () => {
    if (!taskId) return;
    try {
      setLoading(true);
      setError(null);
      setPreviewFile(null);
      const response = await apiGet(`/TaskTracking/GetAttachments/${taskId}`);
      if (response?.data) {
        const formattedAttachments = response.data.map((attachment) => ({
          ...attachment,
          url: `${process.env.REACT_APP_API_BASE_URL}/uploads/tasks/TaskUpload/${attachment.fileName}`,
          fileSize: attachment.fileSize || 0,
        }));
        setAttachments(formattedAttachments);
        if (formattedAttachments.length > 0) {
          setPreviewFile(formattedAttachments[0]);
        }
      } else {
        setAttachments([]);
        toast.error("No attachments found");
      }
    } catch (error) {
      console.error("Error fetching attachments:", error);
      setError(error.response?.data?.message || "Failed to load attachments");
      toast.error("Failed to load attachments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show && taskId) {
      fetchAttachments();
    }
  }, [show, taskId]);

  return (
    <>
      <Modal show={show} onHide={handleClose} size="xl" centered>
        <Modal.Header
          closeButton
            className="modal-customheader"
          closeVariant="white"
        >
          <Modal.Title className="custom-modaltitle">
            <h5 className="mb-0">
               Attachments{taskId && ` (TaskID ${taskId})`}
            </h5>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="custommodal-body">
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="btn-primary" />
              <p className="mt-2">Loading attachments...</p>
            </div>
          ) : error ? (
            <Alert variant="danger">
              <i className="bi bi-exclamation-triangle-fill me-2" />
              {error}
            </Alert>
          ) : attachments.length > 0 ? (
            <Row>
              <Col md={4}>
                <ListGroup
                  className="mb-3"
                  style={{ maxHeight: "500px", overflowY: "auto" }}
                >
                  {attachments.map((file, index) => (
                    <ListGroup.Item
                      key={file.attachmentID || index}
                      action
                      active={previewFile?.attachmentID === file.attachmentID}
                      onClick={() => setPreviewFile(file)}
                      className="d-flex justify-content-between align-items-center"
                    >
                      <div className="d-flex align-items-center">
                        {getFileIcon(file.fileName)}
                        <span
                          className="ms-2 text-truncate"
                          style={{ maxWidth: "200px" }}
                        >
                          {file.fileName}
                        </span>
                      </div>
                      <div className="d-flex align-items-center">
                        <small className="text-muted me-2">
                          {formatFileSize(file.fileSize)}
                        </small>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Col>
              <Col md={8}>
                <div className="border rounded p-3 h-100 d-flex flex-column custombox">
                  <div className="flex-grow-1 d-flex align-items-center justify-content-center bg-light rounded mb-3">
                    {renderFilePreview(previewFile)}
                  </div>
                  <div className="d-flex justify-content-end gap-2">
                    <Button
                      variant="secondary"
                         className="btn-fontweight smallcancelbtn-fontsize"
                      onClick={() => window.open(previewFile?.url, "_blank")}
                      disabled={!previewFile}
                    >
                      <i className="bi bi-box-arrow-up-right me-1" /> Open
                    </Button>
                    <Button
                    className="btn-fontweight addbtn-fontsize"
                      variant="btn-primary"
                      onClick={() => downloadFile(previewFile)}
                      disabled={!previewFile}
                    >
                      <i className="bi bi-download me-1" /> Download
                    </Button>
                  </div>
                </div>
              </Col>
            </Row>
          ) : (
            <div className="text-center py-4">
              <i className="bi bi-folder-x fs-1 text-muted" />
              <p className="mt-2">No attachments available</p>
            </div>
          )}
        </Modal.Body>
        {/* <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
        </Modal.Footer> */}
      </Modal>
    </>
  );
};

export default AttachmentsModalTrash;
